/**
 * @file ReceiptItemsList.tsx
 * @description Позиции приёмки: карточки на узком экране, таблица на широком
 * (phase19.13 этап 0 — позиция = фасовка; единый шаблон документа —
 * phase19.24 этап 1, шаг 1; этикетки и срок «мягко» — phase19.22).
 *
 * Колонки документа: «Партия», «Годен до», «Ячейка», «Кол-во», у проведённой
 * приёмки — «Этикетки» (✓ дата / устарели / —). Цены стоят третьей строкой в
 * колонке товара, а не своими колонками.
 *
 * phase19.31:
 *  - SC51: `showSalePrice={false}` — кладовщик цену продажи не видит и в
 *    строках документа (закупочная остаётся);
 *  - Б15: партия и срок — отдельными колонками (или второй линией, см. ниже),
 *    срок виден всегда, когда он есть; у черновика без номера партии — «присвоит система»;
 *  - Б2: у черновика (рядом боковая панель) таблица — с 1280, до этого
 *    карточки: на 1024 колонке позиций остаётся ~370px, и таблица
 *    прокручивалась, пряча «Ячейку» и «Кол-во»;
 *  - приёмка qa4 п. 4: где места мало (черновик с панелью; проведённая на
 *    1024–1279), «Годен до» — второй линией под партией, фото не рисуем.
 *
 * Действия строки живут в «⋯» (правило A6): у черновика — «Изменить
 * позицию» первым (Б16: количество, партия, ячейка, даты без удаления
 * строки), «Указать / Сменить ячейку» (в таблице — ещё и прямо в колонке
 * «Ячейка») и «Удалить» последним; у проведённой — «Указать срок», только
 * если срок пуст (W20).
 */

import { CalendarPlus, MapPin, Pencil, Trash2 } from "lucide-react";

import { DocumentCard } from "../ui/DocumentCard";
import { DocumentItemsTable } from "../ui/DocumentItemsTable";
import { RowActionsMenu, type RowAction } from "../ui/RowActionsMenu";
import { receiptItemColumns } from "./receipt-item-columns";
import { receiptBinActionLabel, receiptExpiryActionLabel, receiptLotLine } from "./receipt-item-format";
import { ReceiptItemBadges } from "./ReceiptItemBadges";
import { WmsSkuThumb } from "./WmsSkuThumb";
import type { LotLabelStatus } from "../../lib/api-labels";
import { formatSkuName, type GoodsReceiptItemDto } from "../../lib/api-wms";
import { useMediaQuery, WIDE_QUERY } from "../../hooks/useMediaQuery";
import { cn } from "@/lib/utils";

/** Этикетки строки: статус партии и дата печати. */
export interface ReceiptItemLabelInfo {
  status: LotLabelStatus | null;
  printedAt: string | null;
}

interface Props {
  items: GoodsReceiptItemDto[];
  highlightedId?: string | null;
  rowDomId?: (itemId: string) => string;
  /** Черновик — позиции можно удалять и править срок. */
  editable: boolean;
  onRemove: (itemId: string) => void;
  /** Строки, чья партия будет/стала «срок не указан» (W20). */
  missingExpiryIds?: string[];
  /** Этикетки строки (только проведённая приёмка). */
  labelOf?: (item: GoodsReceiptItemDto) => ReceiptItemLabelInfo | undefined;
  /** «Указать / Изменить срок» (право приёмки). */
  onSetExpiry?: (item: GoodsReceiptItemDto) => void;
  /** Показывать цену продажи; в режиме кладовщика — нет (SC51). */
  showSalePrice?: boolean;
  /** Черновик: «Изменить позицию» (Б16). */
  onEdit?: (item: GoodsReceiptItemDto) => void;
  /** Черновик: «Указать / Сменить ячейку» (Б16). */
  onSetBin?: (item: GoodsReceiptItemDto) => void;
}

/** «закуп 120 ₽ · продажа 240 ₽» — пусто, если цены не заданы. */
function priceText(item: GoodsReceiptItemDto, showSalePrice: boolean): string | null {
  const parts: string[] = [];
  if (item.unitCost) parts.push(`закуп ${item.unitCost} ₽`);
  if (showSalePrice && item.salePrice) parts.push(`продажа ${item.salePrice} ₽`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** Пустой черновик — подсказка, где добавить строку. */
const EMPTY_DRAFT = "Позиций пока нет. Добавьте первую через панель «Добавить строку» или отсканируйте артикул.";

export function ReceiptItemsList({
  items,
  highlightedId = null,
  rowDomId,
  editable,
  onRemove,
  missingExpiryIds = [],
  labelOf,
  onSetExpiry,
  showSalePrice = true,
  onEdit,
  onSetBin,
}: Props) {
  const missing = new Set(missingExpiryIds);
  const wide = useMediaQuery(WIDE_QUERY);
  // Правка строки и ячейки — только у черновика.
  const edit = editable ? onEdit : undefined;
  const setBin = editable ? onSetBin : undefined;

  function actionsOf(item: GoodsReceiptItemDto): RowAction[] {
    const actions: RowAction[] = [];
    if (edit) actions.push({ key: "edit", label: "Изменить позицию", icon: Pencil, run: () => edit(item) });
    if (setBin) actions.push({ key: "bin", label: receiptBinActionLabel(item), icon: MapPin, run: () => setBin(item) });
    const expiryLabel = onSetExpiry ? receiptExpiryActionLabel(item, editable, missing.has(item.id)) : null;
    if (expiryLabel && onSetExpiry) {
      actions.push({ key: "expiry", label: expiryLabel, icon: CalendarPlus, run: () => onSetExpiry(item) });
    }
    if (editable) {
      actions.push({ key: "remove", label: "Удалить позицию", icon: Trash2, danger: true, run: () => onRemove(item.id) });
    }
    return actions;
  }

  function rowActions(item: GoodsReceiptItemDto) {
    return (
      <RowActionsMenu
        actions={actionsOf(item)}
        title={`Действия с позицией ${item.sku?.code ?? ""}`}
        ariaLabel="Действия с позицией"
        size="compact"
      />
    );
  }

  function badges(item: GoodsReceiptItemDto, variant: "card" | "cell") {
    const label = labelOf?.(item);
    return (
      <ReceiptItemBadges
        labelStatus={label ? label.status : undefined}
        printedAt={label?.printedAt}
        missingExpiry={missing.has(item.id)}
        variant={variant}
      />
    );
  }

  // Узко для отдельной колонки срока: черновик (рядом панель) или 1024–1279.
  const tight = editable || !wide;
  const columns = receiptItemColumns({
    draft: editable,
    isMissing: (item) => missing.has(item.id),
    labelsCell: labelOf ? (item) => badges(item, "cell") : undefined,
    onSetBin: setBin,
    foldExpiry: tight,
    // 1024–1279: «Этикетки» — второй линией под названием (QA-5 D9).
    foldLabels: !wide,
  });

  // Черновик: рядом панель «Добавить строку» — таблица только с 1280.
  const breakpoint = editable ? "xl" : "lg";

  return (
    <>
      <ul className={cn("flex flex-col gap-2", breakpoint === "xl" ? "xl:hidden" : "lg:hidden")}>
        {items.length === 0 ? (
          // Тот же текст, что у таблицы: на телефоне под «Позиции (0)» не было ничего.
          <li className="rounded-xl bg-surface-lowest px-4 py-6 text-sm text-on-surface-variant ring-ghost">
            {editable ? EMPTY_DRAFT : "Документ проведён без позиций."}
          </li>
        ) : null}
        {items.map((item) => {
          const actions = actionsOf(item);
          return (
            <li key={item.id} data-row-id={rowDomId?.(item.id)}>
              <DocumentCard
                tone={highlightedId === item.id ? "selected" : "default"}
                media={<WmsSkuThumb />}
                title={formatSkuName(item.sku)}
                code={item.sku?.code ?? item.skuId}
                qty={item.quantity}
                meta={[receiptLotLine(item, editable), `Ячейка: ${item.bin?.code ?? "—"}`, priceText(item, showSalePrice), badges(item, "card")]}
                actions={actions.length > 0 ? rowActions(item) : undefined}
              />
            </li>
          );
        })}
      </ul>

      <DocumentItemsTable
        items={items}
        breakpoint={breakpoint}
        // Черновик с панелью: колонке позиций на 1280 остаётся ~584px, и
        // заглушка фото 80px отнимала место у «Партии» и «Годен до».
        hideMedia={tight}
        rowKey={(item) => item.id}
        rowId={rowDomId ? (item) => rowDomId(item.id) : undefined}
        rowTone={(item) => (highlightedId === item.id ? "selected" : "default")}
        columns={columns}
        media={() => <WmsSkuThumb />}
        title={(item) => formatSkuName(item.sku)}
        code={(item) => item.sku?.code ?? item.skuId}
        note={(item) => {
          const prices = priceText(item, showSalePrice);
          const folded = labelOf && !wide ? badges(item, "cell") : null;
          if (!prices && !folded) return null;
          return (
            <span className="flex flex-col gap-1">
              {prices ? <span className="text-xs text-on-surface-variant">{prices}</span> : null}
              {folded}
            </span>
          );
        }}
        actions={rowActions}
        emptyText={editable ? EMPTY_DRAFT : "Документ проведён без позиций."}
      />
    </>
  );
}
